import { getAuth } from 'firebase/auth';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { switchContext, switchContextAndVerify } from '../../api/memberships/switchContext';
import { logError } from '../../rollbar';
import { showSuccessToast } from '../Shared/showToast';
import {
  autoAlignAttemptedRecently,
  getIdTokenWhenReady,
  markAutoAlignAttempted,
  ROLE_CONTEXT_CHANGED_EVENT,
  setHasProviderMembership,
  setPendingAlignToast,
  takePendingAlignToast,
  withSchoolName,
} from './roleContext';

const NOVA_URL = import.meta.env.VITE_WS_MARKETPLACE_APP_URL || '';

// Visibility refetches are throttled so tab-switching cannot hammer the memberships endpoint.
const REFETCH_THROTTLE_MS = 30_000;

interface Membership {
  id: string;
  identity_id: string;
  context_type: string;
  context_id: string;
  role: string;
  org_name?: string | null;
}

interface MembershipsResponse {
  memberships: Membership[];
  active_membership_id: string | null;
}

const ROLE_LABELS: Record<string, string> = {
  director: 'Director',
  location_admin: 'Location Admin',
  teacher: 'Teacher',
  support_staff: 'Support Staff',
  parent: 'Parent',
  guardian: 'Guardian',
  emergency_contact: 'Emergency Contact',
};

const membershipLabel = (m: Membership) => withSchoolName(m.org_name, ROLE_LABELS[m.role] || m.role);

// Plain-language copy for the two ways this can fail, kept as translation keys like the
// rest of this component's strings. Detail for debugging goes to the console, not the screen.
const LOAD_FAILED_MESSAGE = 'We could not load your roles. Please refresh the page.';
const CLAIMS_NOT_READY_MESSAGE =
  'We saved your new role, but it is not ready yet. Please refresh the page. If you still see your old role, sign out and sign in again.';

/**
 * Reports a failed response to Rollbar with enough detail to debug it: status, URL and body.
 * Never logs the Firebase token or the Authorization header.
 */
async function logResponseFailure(what: string, url: string, res: Response) {
  const body = await res.text().catch(() => '');
  logError(`RoleSwitcher: ${what}`, null, { url, status: res.status, body });
}

/** The one place failures are shown, anchored under the switcher button. */
function RoleSwitcherMessage({ message }: { message: string }) {
  return (
    <div
      role="alert"
      data-testid="role-switcher-error"
      className="absolute right-0 top-full z-50 mt-1 w-64 rounded-lg border border-red-300 bg-white p-3 text-sm text-red-600 shadow-lg"
    >
      {message}
    </div>
  );
}

/**
 * Role switcher dropdown for the CCMS header.
 * Shows only when the user has 2+ active memberships and multi-role is enabled.
 * Calls the Nova firebase-api to fetch memberships and switch context.
 */
export function RoleSwitcher() {
  const { t } = useTranslation();
  const [memberships, setMemberships] = useState<Membership[]>([]);
  const [activeMembershipId, setActiveMembershipId] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Guards: the auto-align runs at most once per mount, and nothing touches state
  // after unmount.
  const autoAlignAttemptedRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Auto-align reloads the page, so its toast has to be shown on the way back in.
  useEffect(() => {
    const roleLabel = takePendingAlignToast();
    if (roleLabel) showSuccessToast(t('Role switched'), t('Switched back to {{role}}', { role: roleLabel }));
  }, [t]);

  // CCMS is the provider app. If the server's active context is still a family role
  // (the user switched to Parent, visited the parent app, then navigated back here)
  // while they also hold a provider-side role, silently switch the server back to the
  // best non-family membership — prefer director, else the first. Family-only users
  // are left alone; CCMS's own auth handles them. This is automatic, so a failure
  // only logs to Rollbar and leaves the pill showing the true server state — never an
  // error message the user did nothing to cause.
  const autoAlignContext = useCallback(async (data: MembershipsResponse) => {
    // The ref guards this mount; the parked attempt guards the mount after the reload, where a
    // fresh ref would otherwise let a switch that never moves the context reload us forever.
    if (autoAlignAttemptedRef.current || autoAlignAttemptedRecently()) return;

    const active = data.memberships.find((m) => m.id === data.active_membership_id);
    if (!active || active.context_type !== 'family') return;

    const nonFamily = data.memberships.filter((m) => m.context_type !== 'family');
    if (nonFamily.length === 0) return;

    autoAlignAttemptedRef.current = true;
    markAutoAlignAttempted();
    const target = nonFamily.find((m) => m.role === 'director') ?? nonFamily[0];

    try {
      const result = await switchContext(target.id);
      if (result.status === 'signed_out') return;

      if (result.status === 'not_found' || result.status === 'error') {
        logError('RoleSwitcher: auto-align switch failed', null, {
          membershipId: target.id,
          status: result.httpStatus,
          body: result.body,
        });
        return;
      }

      // A 200 is not proof the context moved. An idempotent no-op or a server that re-pins the
      // family context answers 200 with family still active, and reloading on that loops forever.
      const activeContextType = result.active?.context_type ?? null;
      if (!activeContextType || activeContextType === 'family') {
        logError('RoleSwitcher: auto-align did not move the active context', null, {
          membershipId: target.id,
          activeContextType,
        });
        return;
      }

      // The claims the app routes on are read once at boot, so only a reload applies them.
      // The toast is parked to show after it.
      const roleLabel = ROLE_LABELS[target.role] || target.role;
      setPendingAlignToast(roleLabel);
      window.dispatchEvent(new Event(ROLE_CONTEXT_CHANGED_EVENT));
      window.location.reload();
    } catch (err) {
      logError('RoleSwitcher: auto-align switch failed', err, { membershipId: target.id });
    }
  }, []);

  const fetchMemberships = useCallback(async () => {
    const url = `${NOVA_URL}/firebase-api/memberships`;

    try {
      const token = await getIdTokenWhenReady();
      if (!token || !mountedRef.current) return;

      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // 404 is the :multi_role_accounts flag being off for this user. That is not a
      // failure, so stay hidden and stay quiet — but still answer the question a guard
      // may be holding on: with no memberships there is nothing to auto-align to.
      if (res.status === 404) {
        setHasProviderMembership(false);
        return;
      }

      // Anything else (500, 403, expired token) is a real failure and must leave a trace.
      if (!res.ok) {
        await logResponseFailure('could not load memberships', url, res);
        if (mountedRef.current) setError(t(LOAD_FAILED_MESSAGE));
        return;
      }

      const data: MembershipsResponse = await res.json();
      if (!mountedRef.current) return;

      setMemberships(data.memberships);
      setActiveMembershipId(data.active_membership_id);
      // A refetch that works clears the earlier failure; otherwise one bad cold-load fetch
      // parked a red message in the header for the rest of the session.
      setError(null);
      // Tell RestrictedRoute whether an auto-align can happen for this user, so the
      // eject does not race it on a guarded CCMS route (MP-6832).
      setHasProviderMembership(data.memberships.some((m) => m.context_type !== 'family'));
      await autoAlignContext(data);
    } catch (err) {
      // A thrown fetch is a network or CORS failure, never "flag off".
      logError('RoleSwitcher: could not load memberships', err, { url, error: err });
      if (mountedRef.current) setError(t(LOAD_FAILED_MESSAGE));
    }
  }, [t, autoAlignContext]);

  // Fetch memberships once Firebase auth has restored the session. On a fresh
  // page load getAuth().currentUser can still be null when this mounts —
  // TopNavigation's isReady gate watches the SSO auth app, not this one — and a
  // plain mount fetch would bail on the missing token and strand the switcher
  // hidden. Signed-out stays quiet: the nav is gated out in that case anyway.
  useEffect(() => {
    const unsubscribe = getAuth().onAuthStateChanged((user) => {
      if (user) fetchMemberships();
    });
    return unsubscribe;
  }, [fetchMemberships]);

  // The mount snapshot goes stale when the user visits the parent app and comes back —
  // bfcache restores and tab switches do not remount. Refetch when the tab becomes
  // visible again, at most once per REFETCH_THROTTLE_MS.
  const lastRefetchRef = useRef(0);
  useEffect(() => {
    const onReturnToTab = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefetchRef.current < REFETCH_THROTTLE_MS) return;
      lastRefetchRef.current = Date.now();
      fetchMemberships();
    };

    document.addEventListener('visibilitychange', onReturnToTab);
    window.addEventListener('focus', onReturnToTab);
    return () => {
      document.removeEventListener('visibilitychange', onReturnToTab);
      window.removeEventListener('focus', onReturnToTab);
    };
  }, [fetchMemberships]);

  const handleSwitch = useCallback(
    async (membershipId: string) => {
      if (membershipId === activeMembershipId || isSwitching) return;

      setIsSwitching(true);
      setError(null);

      try {
        const result = await switchContextAndVerify(membershipId);

        if (result.status === 'ok' || result.status === 'unavailable') {
          // Family roles live in the parent app, not CCMS. Everything else reloads to pick up the new role.
          if (result.active?.context_type === 'family') {
            window.location.href = `${NOVA_URL}/parents`;
          } else {
            window.location.reload();
          }
          return;
        }

        if (result.status === 'superseded') {
          window.location.reload();
          return;
        }

        if (result.status === 'signed_out') {
          logError('RoleSwitcher: no signed-in user for switch', null, { membershipId });
          setError(t('Your session ended. Please sign in again to use your new role.'));
        } else if (result.status === 'not_ready') {
          // Reloading here would quietly run the page as the old role.
          logError('RoleSwitcher: claims not updated after refresh', null, { membershipId, ...result });
          setError(t(CLAIMS_NOT_READY_MESSAGE));
        } else {
          setError(result.error || t('Switch failed. Please try again.'));
        }
        setIsSwitching(false);
      } catch {
        setError(t('Switch failed. Please try again.'));
        setIsSwitching(false);
      }
    },
    [activeMembershipId, isSwitching, t]
  );

  // Don't render if less than 2 memberships — unless loading them failed, in which case
  // say so rather than vanishing with no trace.
  if (memberships.length < 2) {
    if (!error) return null;
    return (
      <div className="relative">
        <RoleSwitcherMessage message={error} />
      </div>
    );
  }

  const activeMembership = memberships.find((m) => m.id === activeMembershipId);
  const activeLabel = activeMembership ? membershipLabel(activeMembership) : t('Select role');

  return (
    <div className="relative" data-testid="role-switcher">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        disabled={isSwitching}
        className="flex items-center gap-1.5 rounded-full border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50 focus:outline-none focus-visible:outline-2 focus-visible:outline-blue-500"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={t('Switch role')}
      >
        {!isSwitching && activeMembership?.org_name ? (
          // Only the school name gets cut off, so the role always shows.
          <span className="flex min-w-0 max-w-48" title={activeLabel}>
            <span className="truncate">{activeMembership.org_name}</span>
            <span className="shrink-0 whitespace-pre">{` · ${ROLE_LABELS[activeMembership.role] || activeMembership.role}`}</span>
          </span>
        ) : (
          <span className="max-w-48 truncate">{isSwitching ? t('Switching...') : activeLabel}</span>
        )}
        <svg className="h-4 w-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && !isSwitching && (
        <div
          className="absolute right-0 top-full z-50 mt-1 w-64 rounded-lg border border-gray-300 bg-white p-2 shadow-lg"
          role="listbox"
          aria-label={t('Switch role')}
        >
          {memberships.map((membership) => (
            <button
              key={membership.id}
              type="button"
              role="option"
              disabled={isSwitching}
              aria-selected={membership.id === activeMembershipId}
              onClick={() => {
                setIsOpen(false);
                handleSwitch(membership.id);
              }}
              className={`w-full rounded px-3 py-2 text-left text-sm ${
                membership.id === activeMembershipId
                  ? 'bg-blue-50 font-medium text-blue-700'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {membershipLabel(membership)}
              {membership.id === activeMembershipId && (
                // Decorative only — the active role is conveyed to assistive tech by
                // aria-selected on this option, and visually by the label + highlight.
                // eslint-disable-next-line i18next/no-literal-string
                <span className="ml-1 text-blue-500" aria-hidden="true">
                  ✓
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Failures show whether or not the dropdown is open — it closes before a switch starts */}
      {error && !isOpen && <RoleSwitcherMessage message={error} />}

      {/* Close dropdown when clicking outside. NOT portalled: the menu above renders inside
          Sidebar.Pusher, whose inline transform makes it a stacking context, so a body-level
          backdrop paints above the menu regardless of the z-index the menu carries and swallows
          every click on it — the options stop being clickable. Keeping the backdrop in the same
          stacking context is what lets the menu's z-50 beat its z-40. The cost is that clicking
          a left-nav link navigates with the dropdown still open, which is cosmetic. */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          data-testid="role-switcher-backdrop"
          onClick={() => setIsOpen(false)}
          aria-hidden="true"
        />
      )}
    </div>
  );
}

export default RoleSwitcher;
